import * as Sentry from '@sentry/react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { useEffect } from 'react'

const SENSITIVE_KEYS = new Set([
  'password',
  'newPassword',
  'currentPassword',
  'refreshToken',
  'accessToken',
  'token',
])

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const headers = event.request?.headers
  if (headers) {
    delete headers.authorization
    delete headers.Authorization
  }
  const data = event.request?.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    for (const key of SENSITIVE_KEYS) {
      if (key in (data as Record<string, unknown>)) {
        ;(data as Record<string, unknown>)[key] = '[Filtered]'
      }
    }
  }
  return event
}

export function getSentryDsn(): string {
  return (import.meta.env.VITE_SENTRY_DSN ?? '').trim()
}

export function initSentry(): void {
  const dsn = getSentryDsn()
  if (!dsn) return

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? '').trim() || import.meta.env.MODE
  const release = (import.meta.env.VITE_SENTRY_RELEASE ?? '').trim() || undefined
  const tracesSampleRate = import.meta.env.PROD ? 0.1 : 1

  Sentry.init({
    dsn,
    environment,
    release,
    enabled: true,
    tracesSampleRate,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    beforeSend: scrubEvent,
  })
}

type AdminSentryUser = {
  id?: number | string
  email?: string
  role?: string
  organisationId?: number | string
}

/** Dev-only: visit any page with `?sentry_test=1` to send a test event. */
export function maybeTriggerDevSentryTest(): void {
  if (!import.meta.env.DEV || !getSentryDsn()) return
  if (new URLSearchParams(window.location.search).get('sentry_test') !== '1') return

  Sentry.captureException(
    new Error('Sentry develop test error (kartly-store-admin)'),
    { tags: { source: 'dev_sentry_test' } },
  )
}

export function syncAdminSentryUser(user: AdminSentryUser | null): void {
  if (!getSentryDsn()) return
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: String(user.id ?? user.email ?? 'unknown'),
    email: user.email,
  })
  if (user.role) Sentry.setTag('user.role', user.role)
  if (user.organisationId != null) {
    Sentry.setTag('organisation.id', String(user.organisationId))
  }
}

export { Sentry }
